/**
 * useSpacePriorityTemplate
 *
 * Loads the priority template configured for a Space and falls back to the
 * default Losi priorities when the Space has no custom template.
 *
 * Primary responsibilities:
 * - Fetch `priority_template` from the current Space record
 * - Expose the ordered priority ids used by task forms and pickers
 *
 * Dependencies:
 * - Authenticated Space API
 */
"use client"

import { useEffect, useState } from "react"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"

interface PriorityTemplateItem {
  id: string
  name: string
  label: string
  color: string
  iconColor: string
  order: number
}

interface PriorityTemplate {
  id: string
  name: string
  description?: string
  priorities: PriorityTemplateItem[]
  isDefault?: boolean
  isCustom?: boolean
}

const defaultPriorityTemplate: PriorityTemplate = {
  id: "standard",
  name: "Standard Priorities",
  description: "Low, Normal, High, Urgent",
  isDefault: true,
  priorities: [
    {
      id: "low",
      name: "Low",
      label: "Low",
      color: "bg-gray-100 text-gray-800 border-gray-200",
      iconColor: "text-gray-500",
      order: 0
    },
    {
      id: "medium",
      name: "Normal",
      label: "Normal",
      color: "bg-blue-100 text-blue-800 border-blue-200",
      iconColor: "text-blue-500",
      order: 1
    },
    {
      id: "high",
      name: "High",
      label: "High",
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      iconColor: "text-yellow-500",
      order: 2
    },
    {
      id: "urgent",
      name: "Urgent",
      label: "Urgent",
      color: "bg-red-100 text-red-800 border-red-200",
      iconColor: "text-red-500",
      order: 3
    }
  ]
}

export const useSpacePriorityTemplate = (spaceId: string) => {
  const [priorityTemplate, setPriorityTemplate] = useState<PriorityTemplate | null>(null)
  const [availablePriorities, setAvailablePriorities] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  useEffect(() => {
    const fetchPriorityTemplate = async () => {
      if (!spaceId) {
        setIsLoading(false)
        return
      }

      try {
        const response = await authenticatedFetch(`/api/spaces/${spaceId}`, {
          cache: "no-store"
        })

        if (!response.ok) {
          throw new Error("Failed to load priority template")
        }

        const data = (await response.json()) as {
          priority_template?: PriorityTemplate | null
        } | null

        if (data?.priority_template) {
          const template = data.priority_template
          setPriorityTemplate(template)
          setAvailablePriorities(
            template.priorities
              ?.slice()
              .sort((left, right) => left.order - right.order)
              .map((priority) => priority.id) ?? []
          )
          return
        }

        setPriorityTemplate(defaultPriorityTemplate)
        setAvailablePriorities(
          defaultPriorityTemplate.priorities.map((priority) => priority.id)
        )
      } catch (error) {
        console.error("Error fetching priority template:", error)
        setPriorityTemplate(defaultPriorityTemplate)
        setAvailablePriorities(
          defaultPriorityTemplate.priorities.map((priority) => priority.id)
        )
      } finally {
        setIsLoading(false)
      }
    }

    void fetchPriorityTemplate()
  }, [refreshTrigger, spaceId])

  const refresh = () => {
    setRefreshTrigger((current) => current + 1)
  }

  return {
    priorityTemplate,
    availablePriorities,
    isLoading,
    refresh
  }
}
